import { ActionTree } from 'vuex';
import router from '../../../router/index';
import Api, { Error as ApiError } from '../../../api/index';
import ApiUrl from '../../../api/url';
import ScreenUrl from '../../../router/url';
import { AuthState, User } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { checkFatalError } from '../../fatal-error-filter';
import { isHappenedOtherPartyError } from '../../error-type';
import { ErrorType } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';

/**
 * オプションパラメータ
 *  * return_before_send API送信前のエラーを共通側で処理せず返す
 */
type Option = { return_before_send?: boolean };

/**
 * パラメーターから認証情報オブジェクトを生成し取得する
 * @param payload リクエストペイロード
 * @param resData APIレスポンスボディ
 * @returns {Object}
 */
const createAuthDataObject = (payload, resData): Object => ({
    id: resData.id,
    uid: payload.uid,
    name: resData.name,
    authorities: resData.authorities,
});

/**
 * APIを直接呼び出す関数（ログイン・ログアウトなど）でのエラーハンドリング用関数
 * @param {any} dispatch Vuex変数
 * @param {any} commit Vuex変数
 * @param resData APIレスポンスボディ
 * @returns {Promise<void>}
 */
const progressFailureHandler = async ({ dispatch, commit }, resData: any) => {
    try {
        await checkFatalError({ dispatch }, resData);
        // アプリケーションエラー時
        await dispatch('process', { running: false, msgKey: '' }, { root: true });
        commit(types.PROGRESS_FAILURE, resData.errors[0]);
    } catch (error) {
        // システムエラー・予期せぬエラー時
        await dispatch('process', { running: false, msgKey: '' }, { root: true });
        commit(types.PROGRESS_END);
    }
};

/**
 * 中継用API呼び出し関数でのエラーハンドリング用関数
 * @param {any} dispatch Vuex変数
 * @param {any} commit Vuex変数
 * @param resData APIレスポンスボディ
 * @param {Option} option 処理オプション
 * @returns {Promise<any>}
 */
const apiErrorHandler = async ({ dispatch, commit }, resData: ApiError, option?: Option) => {
    try {
        if (resData == null && option?.return_before_send) {
            return Promise.reject(ErrorType.BeforeSendError);
        }
        await checkFatalError({ dispatch }, resData);
        if (resData.status === 401) {
            // セッションタイムアウト時
            await dispatch('toastMessage', resData.errors[0].message, { root: true });
            commit(types.AUTHORIZED_ERROR);
            commit(types.DEAL_NOTICE);
            commit(types.DEAL_ERROR);

            // ログイン画面に強制遷移
            router.push(ScreenUrl.Auth.LOGIN);

            // セッションタイムアウトはアプリケーションエラー扱いとしないためPromise.resolveを返す
            return Promise.resolve(ErrorType.SessionTimeout);
        }
        // アプリケーションエラー時
        return Promise.reject(resData);
    } catch (error) {
        // システムエラー・予期せぬエラー時
        // fatalErrorを上記で既にハンドリングしたので、Promise.resolveを返す
        return Promise.resolve(ErrorType.FatalError);
    }
};

export const actions: ActionTree<AuthState, RootState> = {
    /**
     * ローカルユーザーログイン
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {{uid: string; password: string;}} payload ログイン情報
     * @returns {Promise<void>}
     */
    async login({ dispatch, commit }, payload: { uid: string; password: string; redirect?: string }) {
        try {
            const auth_param = {
                uid: payload.uid,
                password: payload.password,
                domain_used: false,
            };
            commit(types.PROGRESS_START);
            await dispatch('process', { running: true, msgKey: 'Logging' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.AUTHENTICATE, auth_param);
            // 成功時
            commit(types.LOGIN_SUCCESS, createAuthDataObject(payload, resData));
            await dispatch('process', { running: false, msgKey: '' }, { root: true });

            // 成功時
            await dispatch('changeDisplayLanguage', { language: resData.language }, { root: true });

            if (resData.password_expired) {
                const location = {
                    name: 'Password Change Page',
                    params: { token: resData.token },
                };
                router.push(location);
            } else if (payload.redirect) {
                router.push(payload.redirect);
            }
        } catch (resData) {
            // 何らかのエラーが発生
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * ドメインユーザーログイン
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {{uid: string; password: string;}} payload ログイン情報
     * @returns {Promise<void>}
     */
    async ldapLogin({ dispatch, commit }, payload: { uid: string; password: string; redirect?: string }) {
        try {
            const auth_param = {
                uid: payload.uid,
                password: payload.password,
                domain_used: true,
            };
            commit(types.PROGRESS_START);
            await dispatch('process', { running: true, msgKey: 'Logging' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.AUTHENTICATE, auth_param);
            // 成功時
            commit(types.LOGIN_SUCCESS, createAuthDataObject(payload, resData));
            await dispatch('process', { running: false, msgKey: '' }, { root: true });

            await dispatch('changeDisplayLanguage', { language: resData.language }, { root: true });

            if (payload.redirect) {
                router.push(payload.redirect);
            }
        } catch (resData) {
            // 何らかのエラーが発生
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * ローカルユーザー多要素ログイン
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {{uid: string; password: string; onetimePassword: string}} payload ログイン情報
     * @returns {Promise<void>}
     */
    async multifactorLogin(
        { dispatch, commit },
        payload: { uid: string; password: string; onetimePassword: string; redirect?: string }
    ) {
        try {
            const auth_param = {
                uid: payload.uid,
                password: payload.password,
                onetime_password: payload.onetimePassword,
                domain_used: false,
            };
            commit(types.PROGRESS_START);
            await dispatch('process', { running: true, msgKey: 'Logging' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.AUTHENTICATE, auth_param);
            // 成功時
            commit(types.LOGIN_SUCCESS, createAuthDataObject(payload, resData));
            await dispatch('process', { running: false, msgKey: '' }, { root: true });

            await dispatch('changeDisplayLanguage', { language: resData.language }, { root: true });

            if (resData.password_expired) {
                const location = {
                    name: 'Password Change Page',
                    params: { token: resData.token },
                };
                router.push(location);
            } else if (payload.redirect) {
                router.push(payload.redirect);
            }
        } catch (resData) {
            // 何らかのエラーが発生
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * ドメインユーザー多要素ログイン
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {{uid: string; password: string; onetimePassword: string}} payload ログイン情報
     * @returns {Promise<void>}
     */
    async ldapMultifactorLogin(
        { dispatch, commit },
        payload: { uid: string; password: string; onetimePassword: string; redirect: string }
    ) {
        try {
            const auth_param = {
                uid: payload.uid,
                password: payload.password,
                onetime_password: payload.onetimePassword,
                domain_used: true,
            };
            commit(types.PROGRESS_START);
            await dispatch('process', { running: true, msgKey: 'Logging' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.AUTHENTICATE, auth_param);
            // 成功時
            commit(types.LOGIN_SUCCESS, createAuthDataObject(payload, resData));
            await dispatch('process', { running: false, msgKey: '' }, { root: true });

            // 成功時
            await dispatch('changeDisplayLanguage', { language: resData.language }, { root: true });

            if (payload.redirect) {
                router.push(payload.redirect);
            }
        } catch (resData) {
            // 何らかのエラーが発生
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * SAMLユーザーログイン
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async sso({ dispatch, commit }) {
        try {
            commit(types.PROGRESS_START);
            await dispatch('process', { running: true, msgKey: 'Logging' }, { root: true });
            // SAML接続用のURL生成を呼び出す
            const resData = await Api.get(ApiUrl.Saml.SSO);

            // 成功時
            commit(types.SSO_SUCCESS);
            await dispatch('process', { running: false, msgKey: '' }, { root: true });

            // 受け取ったURLでリダイレクトする
            if (resData && resData.idp_authentication_url) {
                location.href = resData.idp_authentication_url;
            }
        } catch (resData) {
            // 何らかのエラーが発生
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * ログアウト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async logout({ dispatch, commit }) {
        try {
            commit(types.PROGRESS_START);
            await Api.post(ApiUrl.Auth.LOGOUT);
            // 成功時
            commit(types.LOGOUT_SUCCESS);
            router.push(ScreenUrl.Auth.LOGIN);
        } catch (resData) {
            // 何らかのエラーが発生
            commit(types.LOGOUT_SUCCESS);
            progressFailureHandler({ dispatch, commit }, resData);
            router.push(ScreenUrl.Auth.LOGIN);
        }
    },

    /**
     * 認証済みのユーザー情報を上書き更新する
     * @param {any} commit Vuex変数
     * @param {{name: string}} payload ユーザー情報
     * @returns {Promise<void>}
     */
    async overwriteUser({ commit }, payload: { name: string }) {
        commit(types.OVERWRITE_USER, payload);
    },

    /**
     * アプリケーションエラーを消す
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async dealError({ commit }) {
        commit(types.DEAL_ERROR);
    },

    /**
     * 認証情報に書き換えがあったことを示すフラグをfalseにする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async dealUpdate({ commit }) {
        commit(types.DEAL_UPDATE);
    },

    /**
     * 表示中の通知情報を消す（確認ボタンや×ボタン押した際など）
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async dealNotice({ commit }) {
        commit(types.DEAL_NOTICE);
    },

    /**
     * 自分の権限リストを取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async myAuthorities({ dispatch, commit }) {
        try {
            commit(types.AUTHORITIES_START);
            const resData = await dispatch('auth/api', { method: 'get', url: ApiUrl.Auth.AUTHORITIES }, { root: true });
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.AUTHORITIES_SUCCESS, resData);
                await dispatch('changeDisplayLanguage', { language: resData.language }, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.AUTHORITIES_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 認証情報を消去する
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearAuthData({ commit }) {
        commit(types.CLEAR_AUTH_DATA);
    },

    /**
     * 認証情報を更新する
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async overwriteAuthData({ commit }, payload: User) {
        commit(types.OVERWRITE_AUTH_DATA, payload);
    },

    /**
     * ハードデバイスとユーザーの紐付け登録
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {{uid: string; password: string; serialNumber: string}} payload ユーザー情報とデバイス情報
     * @returns {Promise<void>}
     */
    async hardDeviceRegister(
        { dispatch, commit, rootState },
        payload: { uid: string; password: string; serial_number: string }
    ) {
        try {
            commit(types.PROGRESS_START);
            await dispatch('process', { running: true, msgKey: 'Registering' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.HARD_DEVICE_REGISTER, payload);

            // 成功時
            const i18n = (rootState as any).i18n;
            const message = translator(i18n, MessageKey.Auth.HARD_DEVICE_REGISTED);
            commit(types.REGISTER_SUCCESS, { message });
            await dispatch('process', { running: false, msgKey: '' }, { root: true });
        } catch (resData) {
            // 何らかのエラーが発生
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * ソフトデバイスとユーザーの紐付け登録
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {{uid: string; password: string;}} payload ユーザー情報
     * @returns {Promise<void>}
     */
    async softDeviceRegister({ dispatch, commit, rootState }, payload: { uid: string; password: string }) {
        try {
            commit(types.PROGRESS_START);
            commit(types.SOFT_REGISTER_START);
            await dispatch('process', { running: true, msgKey: 'Registering' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.SOFT_DEVICE_REGISTER, payload);
            // 成功時
            const i18n = (rootState as any).i18n;
            const message = translator(i18n, MessageKey.Auth.SOFT_DEVICE_REGISTED);
            commit(types.SOFT_REGISTER_SUCCESS, { message: message, secret_key: resData.secret_key });
            await dispatch('process', { running: false, msgKey: '' }, { root: true });
        } catch (resData) {
            // 何らかのエラーが発生
            commit(types.SOFT_REGISTER_FAILURE);
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * ソフトデバイスとユーザーの紐付けのエラーをクリアする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async softDeviceRegisterReset({ commit }) {
        commit(types.SOFT_REGISTER_RESET);
    },

    /**
     * ロック解除メールの送信
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {string} email メールアドレス
     * @returns {Promise<void>}
     */
    async sendUnlockMail({ dispatch, commit, rootState }, email: string) {
        try {
            commit(types.PROGRESS_START);
            commit(types.SEND_MAIL_START);
            await dispatch('process', { running: true, msgKey: 'Sending_Mail' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.SEND_UNLOCK_MAIL, { email: email });
            // 成功時
            const i18n = (rootState as any).i18n;
            const message = translator(i18n, MessageKey.Auth.SEND_UNLOCK_MAIL);
            commit(types.SEND_MAIL_SUCCESS, { message: message });
            await dispatch('process', { running: false, msgKey: '' }, { root: true });
        } catch (resData) {
            // 何らかのエラーが発生
            commit(types.SEND_MAIL_FAILURE);
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * パスワード再設定メールの送信
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {string} email メールアドレス
     * @returns {Promise<void>}
     */
    async sendPasswordResetMail({ dispatch, commit, rootState }, email: string) {
        try {
            commit(types.PROGRESS_START);
            commit(types.SEND_MAIL_START);
            await dispatch('process', { running: true, msgKey: 'Sending_Mail' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.SEND_PASSWORD_RESET_MAIL, { email: email });
            // 成功時
            const i18n = (rootState as any).i18n;
            const message = translator(i18n, MessageKey.Auth.SEND_PASSWORD_RESET_MAIL);
            commit(types.SEND_MAIL_SUCCESS, { message: message });
            await dispatch('process', { running: false, msgKey: '' }, { root: true });
        } catch (resData) {
            // 何らかのエラーが発生
            commit(types.SEND_MAIL_FAILURE);
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * 本人確認メール再送信
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {string} email メールアドレス
     * @returns {Promise<void>}
     */
    async sendUnconfirmedMail({ dispatch, commit, rootState }, email: string) {
        try {
            commit(types.PROGRESS_START);
            commit(types.SEND_MAIL_START);
            await dispatch('process', { running: true, msgKey: 'Sending_Mail' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.SEND_UNCONFIRMED_MAIL, { email: email });
            // 成功時
            const i18n = (rootState as any).i18n;
            const message = translator(i18n, MessageKey.Auth.SEND_UNCONFIRMED_MAIL);
            commit(types.SEND_MAIL_SUCCESS, { message: message });
            await dispatch('process', { running: false, msgKey: '' }, { root: true });
        } catch (resData) {
            // 何らかのエラーが発生
            commit(types.SEND_MAIL_FAILURE);
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * パスワードを忘れた場合のパスワード更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {{ token: string; password: string;} payload ユーザー情報
     * @returns {Promise<void>}
     */
    async updateForgottenPassword({ dispatch, commit, rootState }, payload: { token: string; password: string }) {
        try {
            commit(types.PROGRESS_START);
            commit(types.UPDATE_FORGOTTEN_PASSWORD_START);
            await dispatch('process', { running: true, msgKey: 'Updating_Password' }, { root: true });
            const resData = await Api.post(ApiUrl.Auth.UPDATE_FORGOTTEN_PASSWORD_PATH, payload);

            // 成功時
            const i18n = (rootState as any).i18n;
            const message = translator(i18n, MessageKey.User.PASSWORD_UPDATED);
            commit(types.UPDATE_FORGOTTEN_PASSWORD_SUCCESS, { message: message });
            await dispatch('process', { running: false, msgKey: '' }, { root: true });
            router.push(ScreenUrl.Auth.LOGIN);
        } catch (resData) {
            // 何らかのエラーが発生
            commit(types.UPDATE_FORGOTTEN_PASSWORD_FAILURE);
            progressFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * BFFへのHTTPリクエストを行うための中継用アクション
     * [重要] 他moduleからBFFへリクエストを行う場合は、必ずこのアクションを経由してリクエスト送信すること
     * そうすることで、システムエラーやセッションタイムアウト等の全体系エラーのハンドリングを自動で行ってくれるため、
     * 呼び出し元のmoduleアクションでは全体系エラーのハンドリングを行う必要はない
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {{method: string; url: string; params?: any; headers?: any;}} payload リクエストペイロード情報
     * @param {Option} option 処理オプション
     * @returns {Promise<any>}
     */
    async api(
        { dispatch, commit },
        payload: { method: string; url: string; params?: any; headers?: any; option?: Option }
    ) {
        try {
            if (payload.params === undefined) payload.params = {};
            return await Api[payload.method](payload.url, payload.params, payload.headers);
        } catch (resData) {
            return apiErrorHandler({ dispatch, commit }, resData, payload.option);
        }
    },

    /**
     * BFFへのHTTPリクエストのエラー処理アクション
     * ダウンロードなどの都合により、vuex経由でBFFが呼べない場合のエラー時に呼び出すアクション
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {{resData: ApiError;}} payload エラーレスポンス
     * @param {Option} option 処理オプション
     * @returns {Promise<any>}
     */
    async apiError({ dispatch, commit }, payload: { resData: ApiError; option?: Option }) {
        return apiErrorHandler({ dispatch, commit }, payload.resData, payload.option);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute() {
        /* 認証情報はログイン中は消してはいけない */
    },

    /**
     * ログインしたかのフラグを削除する
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnAfterLogin({ commit }) {
        commit(types.AFTER_LOGIN_CLEAR);
    },
};
